import type { Booking, Company, Incoterm, Supplier } from "../../types/booking";
import { getBookingProducts } from "../../utils/agentBookingMetrics";
import type { CommercialInvoiceAnalysisResult } from "./types";

export type CrossCheckStatus =
  | "match"
  | "mismatch"
  | "missing_booking"
  | "missing_invoice"
  | "skipped";

export interface CrossCheckRow {
  id: string;
  group: string;
  label: string;
  bookingValue: string | null;
  status: CrossCheckStatus;
  note?: string;
}

export interface BookingCrossCheckResult {
  rows: CrossCheckRow[];
  matchCount: number;
  mismatchCount: number;
  issueCount: number;
  summary: string;
}

const firstRelation = <T,>(value: T | T[] | null | undefined): T | null => {
  if (value == null) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
};

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const buildInvoiceHaystack = (analysis: CommercialInvoiceAnalysisResult): string => {
  const evidence = analysis.requiredFields
    .map((field) => field.evidence)
    .filter(Boolean)
    .join(" ");
  return `${analysis.extractedTextPreview ?? ""} ${evidence}`.toLowerCase();
};

const formatNullable = (value: string | number | null | undefined): string | null => {
  if (value == null || value === "") return null;
  return String(value).trim() || null;
};

const findTextInInvoice = (
  value: string | null | undefined,
  haystack: string
): boolean => {
  if (!value?.trim()) return false;
  const normalizedHaystack = normalize(haystack);
  const normalizedValue = normalize(value);
  if (normalizedValue.length >= 3 && normalizedHaystack.includes(normalizedValue)) {
    return true;
  }

  const words = value
    .toLowerCase()
    .split(/[\s,./]+/)
    .map((w) => normalize(w))
    .filter((w) => w.length >= 3);

  if (words.length >= 2) {
    return words.every((word) => normalizedHaystack.includes(word));
  }

  return false;
};

const findHsInInvoice = (hsCode: string | null | undefined, haystack: string): boolean => {
  if (!hsCode?.trim()) return false;
  const digits = hsCode.replace(/\D/g, "");
  if (digits.length < 6) return false;
  const normalizedHaystack = normalize(haystack);
  return normalizedHaystack.includes(digits) || haystack.toLowerCase().includes(hsCode.toLowerCase());
};

const findAmountInInvoice = (amount: number, haystack: string): boolean => {
  if (!Number.isFinite(amount) || amount <= 0) return false;
  const plain = haystack.replace(/,/g, "");
  const candidates = new Set<string>([
    amount.toFixed(2),
    amount.toFixed(0),
    String(Math.round(amount)),
  ]);

  if (amount >= 1000) {
    candidates.add(
      amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  for (const candidate of candidates) {
    if (plain.includes(candidate)) return true;
  }

  return false;
};

const findQuantityInInvoice = (quantity: number, haystack: string): boolean => {
  if (!Number.isFinite(quantity) || quantity <= 0) return false;
  return findAmountInInvoice(quantity, haystack) || haystack.includes(String(quantity));
};

interface BookingSnapshot {
  reference: string | null;
  incoterm: string | null;
  companyName: string | null;
  companyVat: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  supplierName: string | null;
  supplierCountry: string | null;
  supplierAddress: string | null;
  supplierEmail: string | null;
  supplierPhone: string | null;
  totalValue: number;
  totalWeight: number;
  totalPieces: number;
  products: ReturnType<typeof getBookingProducts>;
}

const buildBookingSnapshot = (booking: Booking): BookingSnapshot => {
  const company = firstRelation<Company>(booking.company_info);
  const supplier = firstRelation<Supplier>(booking.supplier_info);
  const incoterm = firstRelation<Incoterm>(booking.booking_incoterm_info);
  const products = getBookingProducts(booking);

  const companyAddress = [
    company?.street,
    company?.house_number,
    company?.postal_code,
    company?.city,
    company?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const supplierAddress = [supplier?.address, supplier?.province, supplier?.country]
    .filter(Boolean)
    .join(", ");

  return {
    reference: formatNullable(booking.reference),
    incoterm: formatNullable(incoterm?.name),
    companyName: formatNullable(company?.name),
    companyVat: formatNullable(company?.vat_number),
    companyAddress: formatNullable(companyAddress),
    companyPhone: formatNullable(company?.phone_number),
    supplierName: formatNullable(supplier?.name),
    supplierCountry: formatNullable(supplier?.country),
    supplierAddress: formatNullable(supplierAddress),
    supplierEmail: formatNullable(supplier?.email),
    supplierPhone: formatNullable(supplier?.phone),
    totalValue: products.reduce((sum, product) => sum + (product.value || 0), 0),
    totalWeight: products.reduce((sum, product) => sum + (product.gross_weight || 0), 0),
    totalPieces: products.reduce((sum, product) => sum + (product.pieces || 0), 0),
    products,
  };
};

const rowStatus = (
  bookingValue: string | null,
  foundInInvoice: boolean,
  invoiceFieldPresent: boolean
): CrossCheckStatus => {
  if (!bookingValue) return "missing_booking";
  if (!invoiceFieldPresent) return "missing_invoice";
  return foundInInvoice ? "match" : "mismatch";
};

export const crossCheckCommercialInvoiceWithBooking = (
  analysis: CommercialInvoiceAnalysisResult,
  booking: Booking
): BookingCrossCheckResult => {
  const haystack = buildInvoiceHaystack(analysis);
  const snapshot = buildBookingSnapshot(booking);
  const rows: CrossCheckRow[] = [];

  const invoiceFieldPresent = (id: string) =>
    analysis.requiredFields.find((field) => field.id === id)?.present ??
    haystack.length > 40;

  const pushTextRow = (
    id: string,
    group: string,
    label: string,
    bookingValue: string | null,
    linkedInvoiceField?: string
  ) => {
    const found = findTextInInvoice(bookingValue, haystack);
    const present = linkedInvoiceField
      ? invoiceFieldPresent(linkedInvoiceField)
      : haystack.length > 40;
    rows.push({
      id,
      group,
      label,
      bookingValue,
      status: rowStatus(bookingValue, found, present),
    });
  };

  pushTextRow("incoterm", "Transport", "Incoterm", snapshot.incoterm, "incoterms");

  pushTextRow("supplier_name", "Supplier (seller)", "Supplier name", snapshot.supplierName, "seller");
  pushTextRow(
    "supplier_country",
    "Supplier (seller)",
    "Supplier country / origin",
    snapshot.supplierCountry,
    "country_of_origin"
  );
  pushTextRow(
    "supplier_address",
    "Supplier (seller)",
    "Supplier address",
    snapshot.supplierAddress,
    "seller"
  );
  pushTextRow("supplier_email", "Supplier (seller)", "Supplier email", snapshot.supplierEmail, "seller");
  pushTextRow("supplier_phone", "Supplier (seller)", "Supplier phone", snapshot.supplierPhone, "seller");

  pushTextRow("company_name", "Company (buyer)", "Company name", snapshot.companyName, "buyer");
  pushTextRow("company_vat", "Company (buyer)", "Company VAT", snapshot.companyVat, "buyer");
  pushTextRow(
    "company_address",
    "Company (buyer)",
    "Company address",
    snapshot.companyAddress,
    "buyer"
  );
  pushTextRow("company_phone", "Company (buyer)", "Company phone", snapshot.companyPhone, "buyer");

  pushTextRow(
    "reference",
    "Booking details",
    "Booking reference",
    snapshot.reference,
    "invoice_number"
  );

  rows.push({
    id: "total_value",
    group: "Products (totals)",
    label: "Total value (USD)",
    bookingValue: snapshot.totalValue > 0 ? `$${snapshot.totalValue.toFixed(2)}` : null,
    status: rowStatus(
      snapshot.totalValue > 0 ? String(snapshot.totalValue) : null,
      findAmountInInvoice(snapshot.totalValue, haystack),
      invoiceFieldPresent("total_value_usd")
    ),
  });

  rows.push({
    id: "total_weight",
    group: "Products (totals)",
    label: "Total gross weight (kg)",
    bookingValue: snapshot.totalWeight > 0 ? `${snapshot.totalWeight} kg` : null,
    status: rowStatus(
      snapshot.totalWeight > 0 ? String(snapshot.totalWeight) : null,
      findQuantityInInvoice(snapshot.totalWeight, haystack),
      invoiceFieldPresent("gross_net_weight")
    ),
  });

  rows.push({
    id: "total_pieces",
    group: "Products (totals)",
    label: "Total pieces",
    bookingValue: snapshot.totalPieces > 0 ? String(snapshot.totalPieces) : null,
    status: rowStatus(
      snapshot.totalPieces > 0 ? String(snapshot.totalPieces) : null,
      findQuantityInInvoice(snapshot.totalPieces, haystack),
      invoiceFieldPresent("quantity_unit")
    ),
  });

  snapshot.products.forEach((product, index) => {
    const prefix = `product_${product.id ?? index}`;
    const productName = formatNullable(product.name);
    const hsCode = formatNullable(product.hs_code);
    const productValue =
      product.value != null && product.value > 0 ? `$${product.value.toFixed(2)}` : null;
    const productWeight =
      product.gross_weight != null && product.gross_weight > 0
        ? `${product.gross_weight} kg`
        : null;
    const productPieces =
      product.pieces != null && product.pieces > 0 ? String(product.pieces) : null;

    rows.push({
      id: `${prefix}_name`,
      group: `Product ${index + 1}`,
      label: "Product name",
      bookingValue: productName,
      status: rowStatus(
        productName,
        findTextInInvoice(productName, haystack),
        invoiceFieldPresent("product_description")
      ),
    });

    rows.push({
      id: `${prefix}_hs`,
      group: `Product ${index + 1}`,
      label: "HS code",
      bookingValue: hsCode,
      status: rowStatus(
        hsCode,
        findHsInInvoice(hsCode, haystack),
        invoiceFieldPresent("hs_code")
      ),
    });

    rows.push({
      id: `${prefix}_value`,
      group: `Product ${index + 1}`,
      label: "Line value (USD)",
      bookingValue: productValue,
      status: rowStatus(
        productValue,
        product.value != null ? findAmountInInvoice(product.value, haystack) : false,
        invoiceFieldPresent("unit_value_usd") || invoiceFieldPresent("total_value_usd")
      ),
    });

    rows.push({
      id: `${prefix}_weight`,
      group: `Product ${index + 1}`,
      label: "Gross weight (kg)",
      bookingValue: productWeight,
      status: rowStatus(
        productWeight,
        product.gross_weight != null
          ? findQuantityInInvoice(product.gross_weight, haystack)
          : false,
        invoiceFieldPresent("gross_net_weight")
      ),
    });

    rows.push({
      id: `${prefix}_pieces`,
      group: `Product ${index + 1}`,
      label: "Pieces",
      bookingValue: productPieces,
      status: rowStatus(
        productPieces,
        product.pieces != null ? findQuantityInInvoice(product.pieces, haystack) : false,
        invoiceFieldPresent("quantity_unit")
      ),
    });
  });

  const matchCount = rows.filter((row) => row.status === "match").length;
  const mismatchCount = rows.filter((row) => row.status === "mismatch").length;
  const issueCount = rows.filter(
    (row) => row.status === "mismatch" || row.status === "missing_invoice"
  ).length;

  const summary =
    issueCount === 0
      ? `${matchCount}/${rows.length} booking fields found on the invoice`
      : `${issueCount} issue${issueCount === 1 ? "" : "s"} — ${matchCount}/${rows.length} matched`;

  return { rows, matchCount, mismatchCount, issueCount, summary };
};
